import {
  BaseRequestInterface, BaseResponseInterface
} from '@interface/public';
import { AxiosAPI } from '@server/_axios';

const apiPath = { // API地址
  user: 'sso/api/v1/' // 用户
};

interface UserInfoInterface { // 用户信息
  token: string; // 会话
  user_id: string; // 用户ID
  name: string; // 名称
  cancelled_at?: string; // 注销申请时间
}

interface LoginInfoRequestInterface {
  login_info?: string; // 登录信息(JSON字符串)
}

export function GetLoginInfo(): string {
  const nav = typeof navigator !== 'undefined' ? navigator : null,
    screenInfo = typeof screen !== 'undefined' ? screen : null,
    connection = nav && (nav as any).connection
      ? (nav as any).connection
      : null,
    nativeDevice = typeof window !== 'undefined'
      ? ((window as any).GFDeviceInfo || (window as any).GFDevice || (window as any).GFNativeDevice || null)
      : null,
    data: any = {
      href: typeof location !== 'undefined' ? location.href : '',
      referrer: typeof document !== 'undefined' ? document.referrer : '',
      user_agent: nav?.userAgent || '',
      platform: nav?.platform || '',
      language: nav?.language || '',
      languages: nav?.languages || [],
      vendor: nav?.vendor || '',
      cookie_enabled: nav?.cookieEnabled || false,
      online: nav?.onLine || false,
      hardware_concurrency: nav?.hardwareConcurrency || '',
      device_memory: (nav as any)?.deviceMemory || '',
      max_touch_points: nav?.maxTouchPoints || 0,
      timezone: (() => {
        try {
          return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
        }
        catch (e: any) {
          return '';
        }
      })(),
      timezone_offset: new Date().getTimezoneOffset(),
      screen: screenInfo
        ? {
          width: screenInfo.width,
          height: screenInfo.height,
          avail_width: screenInfo.availWidth,
          avail_height: screenInfo.availHeight,
          color_depth: screenInfo.colorDepth,
          pixel_depth: screenInfo.pixelDepth,
          device_pixel_ratio: typeof devicePixelRatio !== 'undefined' ? devicePixelRatio : 1
        }
        : null,
      connection: connection
        ? {
          effective_type: connection.effectiveType || '',
          downlink: connection.downlink || '',
          rtt: connection.rtt || '',
          save_data: connection.saveData || false
        }
        : null,
      client: typeof $FN !== 'undefined' ? $FN.Agent.client() : '',
      browser: typeof $FN !== 'undefined' ? $FN.Agent.browser() : '',
      gf_config: typeof window !== 'undefined' && window?.GFConfig
        ? { ...window.GFConfig }
        : null,
      native_device: nativeDevice
    };
  
  return JSON.stringify(data);
}

//----- 获取验证码 Start -----//
export enum AuthUseEnum { // 验证码用途
  None, // 无
  Register, // 注册
  Login, // 登录
  Cancel // 注销
}

export enum AuthTypeEnum { // 验证码类型
  None, // 无
  Phone, // 手机
  Mail // 邮箱
}

export interface RequestAuth extends BaseRequestInterface {
  usage: AuthUseEnum; // 验证码用途
  target_type: AuthTypeEnum; // 验证码类型
  target: string; // 手机号|邮箱
}

export interface ResponseAuth extends BaseResponseInterface {
  data: {
    user_id: string; // 用户ID
  };
}

export async function AxiosAuth(params: RequestAuth): Promise<any> {
  $RQ.PushWebHookPrometheus({
    action: $Enum.PrometheusActionEnum.Code
  });
  
  return AxiosAPI({
    url: `${ apiPath.user }user/code`,
    params: {
      ...params,
      t: Date.now()
    }
  });
}

//----- 获取验证码 End -----//

//----- 验证手机号 Start -----//
export interface RequestCheckPhone extends BaseRequestInterface {
  phone: string; // 手机号
}

export interface ResponseCheckPhone extends BaseResponseInterface {
  data: {
    is_registered: 0 | 1 | 2; // 0 未知 | 1 已经注册 | 2 未注册
  };
}

export async function AxiosCheckPhone(data: RequestCheckPhone): Promise<any> {
  return AxiosAPI({
    url: `${ apiPath.user }user/check_registered`,
    method: 'post',
    data
  });
}

//----- 验证手机号 End -----//

//----- 注册 Start -----//
export interface RequestRegister extends BaseRequestInterface {
  // login_type: LoginTypeEnum; // 登录类型
  phone: string; // 手机号
  code: number | string; // 验证码
  source: string; // 来源
  invitation_code: string; // 邀请
  wechat_uid?: string; // 微信标识
}

export interface ResponseRegister extends BaseResponseInterface {
  data: UserInfoInterface; // 登录信息
}

export async function AxiosRegister(data: RequestRegister): Promise<any> {
  $RQ.PushWebHookPrometheus({
    action: $Enum.PrometheusActionEnum.Register
  });
  
  return AxiosAPI({
    url: `${ apiPath.user }user/register/v2`,
    method: 'post',
    data
  });
}

//----- 注册 End -----//

//----- 登录 Start -----//
export enum LoginTypeEnum { // 登录类型
  None, // 无
  Name, // 用户名
  Phone, // 手机
  Mail// 邮箱
}

export interface RequestLogin extends BaseRequestInterface, LoginInfoRequestInterface {
  login_type: LoginTypeEnum; // 登录类型
  phone: string; // 手机号
  code: number | string; // 验证码
  wechat_uid?: string; // 微信标识
}

export interface ResponseLogin extends BaseResponseInterface {
  data: UserInfoInterface; // 登录信息
}

export async function AxiosLogin(data: RequestLogin): Promise<any> {
  $RQ.PushWebHookPrometheus({
    action: $Enum.PrometheusActionEnum.Login
  });
  
  return AxiosAPI({
    url: `${ apiPath.user }user/login`,
    method: 'post',
    data: {
      login_info: GetLoginInfo(),
      ...data
    }
  });
}

//----- 登录 End -----//

//----- 退出登录 Start -----//
export interface RequestLogout extends BaseRequestInterface {
  token: string; // 会话
}

export interface ResponseLogout extends BaseResponseInterface {
}

export async function AxiosLogout(data: RequestLogout): Promise<any> {
  return AxiosAPI({
    url: `${ apiPath.user }user/logout`,
    method: 'put',
    data
  });
}

//----- 退出登录 End -----//

//----- 刷新凭证 Start -----//
export interface RequestRefresh extends BaseRequestInterface {
}

export interface ResponseRefresh extends BaseResponseInterface {
  data: {
    token: string; // 会话
  }
}

export async function AxiosRefresh(data: RequestRefresh): Promise<any> {
  return AxiosAPI({
    url: `${ apiPath.user }user/refresh`,
    method: 'put',
    data
  });
}

//----- 刷新凭证 End -----//

//----- 用户注销 Start -----//
export interface RequestCancelUser extends BaseRequestInterface {
  phone: string; // 手机号
  code: number | string; // 验证码
  uid: string; // 用户ID
}

export interface ResponseCancelUser extends BaseResponseInterface {
}

export async function AxiosCancelUser(data: RequestCancelUser): Promise<any> {
  return AxiosAPI({
    url: `${ apiPath.user }user/cancel`,
    method: 'post',
    data
  });
}

//----- 用户注销 End -----//

//----- 终止注销 Start -----//
export interface RequestStopCancelUser extends BaseRequestInterface {
  uid: string; // 用户ID
}

export interface ResponseStopCancelUser extends BaseResponseInterface {
  data: UserInfoInterface; // 登录信息
}

export async function AxiosStopCancelUser(data: RequestStopCancelUser): Promise<any> {
  return AxiosAPI({
    url: `${ apiPath.user }user/stop_cancel`,
    method: 'post',
    data
  });
}

//----- 终止注销 End -----//

//----- 检查用户信息 Start -----//
export interface RequestCheckUserInfo extends BaseRequestInterface {
}

export interface ResponseCheckUserInfo extends BaseResponseInterface {
  data: {
    is_writen: boolean; // 是否填写个人信息
  };
}

export async function AxiosCheckUserInfo(params: RequestCheckUserInfo): Promise<any> {
  return AxiosAPI({
    url: `${ apiPath.user }user/check/personal_info`,
    params: {
      ...params,
      t: Date.now()
    }
  });
}

//----- 检查用户信息 End -----//

//----- 补充用户信息 Start -----//
export interface RequestPostUserInfo extends BaseRequestInterface {
  target_type?: AuthTypeEnum; // 验证码类型
  phone?: string; // 手机号
  email?: string; // 邮箱
  code?: number | string; // 验证码
  pwd?: string; // 密码
  name: string; // 用户名
  nick?: string; // 昵称
  real_name: string; // 真实姓名
  ent_name: string; // 企业名称
  position: string; // 职位
  app_id?: string; // 业务标识
}

export interface ResponsePostUserInfo extends BaseResponseInterface {
  data: UserInfoInterface; // 登录信息
}

export async function AxiosPostUserInfo(data: RequestPostUserInfo): Promise<any> {
  return AxiosAPI({
    url: `${ apiPath.user }user/update/personal_info`,
    method: 'post',
    data
  });
}

//----- 补充用户信息 End -----//

//----- 微信登录 Start -----//
export enum WeChatSceneEnum { // 微信场景
  PC = 1, // PC端
  Mobile, // Mobile端
  Applet, // 小程序
  WXBrowser // 微信浏览器
}

export interface RequestWeChatLogin extends BaseRequestInterface, LoginInfoRequestInterface {
  app_id: string; // 微信应用标识
  code: string; // 微信用户标识
  state: string; // 登录状态
  scene: WeChatSceneEnum; // 场景
}

export interface ResponseWeChatLogin extends BaseResponseInterface {
  data: {
    is_bound: boolean; // 是否绑定用户
    
    // 未绑定
    wechat_uid: string; // 微信标识
    
    // 已绑定
    token: string; // 会话
    user_id: string; // 用户ID
    name: string; // 名称
    cancelled_at?: string; // 注销申请时间
  };
}

export async function AxiosWeChatLogin(data: RequestWeChatLogin): Promise<any> {
  return AxiosAPI({
    url: `${ apiPath.user }user/login/wechat`,
    method: 'post',
    data: {
      login_info: GetLoginInfo(),
      ...data
    }
  });
}

//----- 微信登录 End -----//

//----- 微信小程序登录 Start -----//
export interface RequestWeChatMiniProgramLogin extends BaseRequestInterface, LoginInfoRequestInterface {
  app_id: string; // 微信小程序应用标识
  code: string; // 微信用户标识
}

export interface ResponseWeChatMiniProgramLogin extends ResponseWeChatLogin {
}

export async function AxiosWeChatMiniProgramLogin(data: RequestWeChatMiniProgramLogin): Promise<any> {
  return AxiosAPI({
    url: `${ apiPath.user }user/login/wechat_miniprogram`,
    method: 'post',
    data: {
      login_info: GetLoginInfo(),
      ...data
    }
  });
}

//----- 微信小程序登录 End -----//

//----- Face ID登录 Start -----//
export interface RequestFaceIDLogin extends BaseRequestInterface, LoginInfoRequestInterface {
  face_id: string; // Face ID标识
}

export interface ResponseFaceIDLogin extends BaseResponseInterface {
  data: UserInfoInterface; // 登录信息
}

export async function AxiosFaceIDLogin(data: RequestFaceIDLogin): Promise<any> {
  return AxiosAPI({
    url: `${ apiPath.user }user/face_id/login`,
    method: 'post',
    data: {
      login_info: GetLoginInfo(),
      ...data
    }
  });
}

//----- Face ID登录 End -----//
